import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "../../Menu/Navbar";
import Footer from "../Sections/Footer";
import moment from "moment";
import parse from "html-react-parser";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../apis/store";
import { useEffect } from "react";
import { toGetNews } from "../../../apis/actions/news.actions";
import { useTranslation } from "react-i18next";
import { TbArrowUpRight, TbArrowLeft, TbCalendar } from "react-icons/tb";
import "moment/locale/ar";

type NewsItem = {
  id: number;
  name: string;
  summary?: string;
  description?: string;
  details?: string;
  image_1920?: string;
  image?: string;
  date?: string;
  tag_ids?: { name: string }[];
  tags_ids?: { name: string }[];
};

const NewsDetail = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const { list, status } = useSelector((state: RootState) => state.news.news);
  const stateArticle = location.state as NewsItem | null;
  const articleId = searchParams.get("id") || stateArticle?.id;
  const news: NewsItem[] = Array.isArray(list) ? list : [];
  const fromList = news.find((item) => String(item.id) === String(articleId));
  const article = fromList || stateArticle;
  const image = article?.image_1920 || article?.image || "/assets/imgs/page-header/banner5.png";
  const summary = article?.summary || article?.description;
  const tags = article?.tag_ids || article?.tags_ids || [];
  const related = news.filter((item) => item.id !== article?.id).slice(0, 4);
  const date = article?.date && moment(article.date).isValid()
    ? moment(article.date).locale(i18n.language === "ar" ? "ar" : "en").format("D MMMM YYYY")
    : null;

  useEffect(() => {
    dispatch(toGetNews(i18n.language));
  }, [dispatch, i18n.language]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [articleId]);

  const openArticle = (item: NewsItem) => {
    navigate(`/news-detail?id=${item.id}`, { state: item });
  };

  return (
    <>
      <Navbar type="internal" />
      <main className="ac-news-detail" dir={i18n.dir()}>
        {article ? (
          <>
            <div className="ac-news-detail__masthead">
              <div className="container">
                <nav className="ac-news-detail__breadcrumbs" aria-label={t("newsDetail.breadcrumb")}>
                  <Link to="/">{t("menu.home")}</Link>
                  <span aria-hidden="true">/</span>
                  <Link to="/news">{t("menu.news")}</Link>
                </nav>
                <div className="ac-news-detail__intro">
                  <div className="ac-news-detail__meta">
                    {tags[0]?.name && <span className="ac-news-detail__tag">{tags[0].name}</span>}
                    {date && <span className="ac-news-detail__date"><TbCalendar aria-hidden="true" />{date}</span>}
                  </div>
                  <h1>{article.name}</h1>
                  {summary && <div className="ac-news-detail__summary">{parse(summary)}</div>}
                </div>
              </div>
            </div>

            <div className="container ac-news-detail__body">
              <div className="ac-news-detail__hero">
                <img src={image} alt={article.name} onError={(event) => {
                  if (!event.currentTarget.src.endsWith("/assets/imgs/page-header/banner5.png")) {
                    event.currentTarget.src = "/assets/imgs/page-header/banner5.png";
                  }
                }} />
              </div>
              <div className="ac-news-detail__layout">
                <article className="ac-news-detail__article">
                  {article.details && <div className="ac-news-detail__content">{parse(article.details)}</div>}
                  {tags.length > 0 && (
                    <div className="ac-news-detail__tags" aria-label={t("newsDetail.tags")}>
                      {tags.map((tag, index) => <span key={`${tag.name}-${index}`}>{tag.name}</span>)}
                    </div>
                  )}
                  <Link to="/news" className="ac-news-detail__back">
                    <TbArrowLeft aria-hidden="true" />{t("newsDetail.back")}
                  </Link>
                </article>

                {related.length > 0 && (
                  <aside className="ac-news-detail__aside" aria-label={t("newsDetail.related")}>
                    <div className="ac-news-detail__aside-heading">
                      <span>{t("newsDetail.keepReading")}</span>
                      <h2>{t("newsDetail.related")}</h2>
                    </div>
                    <div className="ac-news-detail__related-list">
                      {related.map((item) => (
                        <button type="button" className="ac-news-detail__related" key={item.id} onClick={() => openArticle(item)}>
                          <img src={item.image_1920 || "/assets/imgs/page-header/banner5.png"} alt="" loading="lazy" />
                          <span className="ac-news-detail__related-copy">
                            <strong>{item.name}</strong>
                            {item.date && <small>{moment(item.date).locale(i18n.language === "ar" ? "ar" : "en").format("D MMM YYYY")}</small>}
                          </span>
                          <TbArrowUpRight className="ac-news-detail__related-arrow" aria-hidden="true" />
                        </button>
                      ))}
                    </div>
                    <Link to="/news" className="ac-news-detail__all">{t("home.news.all")} <TbArrowUpRight aria-hidden="true" /></Link>
                  </aside>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="container ac-news-detail__empty">
            {status === "idle" ? <p>{t("btn.loading")}</p> : <p>{t("newsDetail.notFound")}</p>}
            <Link to="/news" className="ac-news-detail__back"><TbArrowLeft aria-hidden="true" />{t("newsDetail.back")}</Link>
          </div>
        )}
      </main>
      <Footer type="external" />
    </>
  );
};

export default NewsDetail;
